import React, { useEffect, useMemo, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet's default icon paths (Webpack/CRA can't resolve the relative
// images in node_modules/leaflet by default). Re-point to CDN urls.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_COLORS = {
  pending: "#9B1C1C",
  assigned: "#7A5E12",
  en_route: "#0B2545",
  on_site: "#0F6D5D",
  completed: "#475569",
};

const buildPinIcon = (color, label) =>
  L.divIcon({
    className: "tj-dispatch-pin",
    html: `<div style="
      width:28px;height:28px;border-radius:50% 50% 50% 0;
      background:${color};border:2px solid #fff;
      box-shadow:0 4px 10px rgba(0,0,0,0.25);
      transform:rotate(-45deg);display:grid;place-items:center;
      font-weight:700;color:#fff;font-size:11px;font-family:system-ui,sans-serif;
    "><span style="transform:rotate(45deg)">${label || ""}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });

export default function DispatchMap({ pins = [], height = 360, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Mount once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const m = L.map(containerRef.current, {
      center: [39.5, -98.35], // US center default; will fit-to-pins if any
      zoom: 4,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(m);
    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers when pins change
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    // Clear
    markersRef.current.forEach((mk) => mk.remove());
    markersRef.current = [];
    if (!pins || pins.length === 0) return;
    const latlngs = [];
    pins.forEach((p, i) => {
      if (typeof p.lat !== "number" || typeof p.lng !== "number") return;
      const color = STATUS_COLORS[p.status] || "#475569";
      const initials = (p.title || "").split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
      const marker = L.marker([p.lat, p.lng], { icon: buildPinIcon(color, initials) }).addTo(m);
      marker.bindPopup(`
        <div style="font-family:system-ui,sans-serif;min-width:180px">
          <div style="font-weight:600;color:#0B2545;font-size:13px;margin-bottom:2px">${p.title || ""}</div>
          <div style="font-size:11px;color:#475569;margin-bottom:6px">${p.subtitle || ""}</div>
          <div style="display:inline-block;padding:2px 6px;border-radius:8px;background:${color}22;color:${color};font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.05em">${p.status || ""}</div>
          ${p.assigned_staff_name ? `<div style="font-size:11px;margin-top:6px;color:#0B2545">Assigned: ${p.assigned_staff_name}</div>` : ""}
        </div>
      `);
      marker.on("click", () => onSelect?.(p.id));
      markersRef.current.push(marker);
      latlngs.push([p.lat, p.lng]);
    });
    if (latlngs.length === 1) {
      m.setView(latlngs[0], 12);
    } else if (latlngs.length > 1) {
      m.fitBounds(L.latLngBounds(latlngs), { padding: [40, 40], maxZoom: 13 });
    }
  }, [pins, onSelect]);

  const empty = useMemo(() => !pins || pins.length === 0, [pins]);

  return (
    <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: "#E2E8F0", height }}
         data-testid="dispatch-map">
      <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
      {empty && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none"
             style={{ background: "rgba(255,255,255,0.85)" }}>
          <div className="text-center px-4">
            <p className="text-sm font-medium" style={{ color: "#0B2545" }}>No mapped jobs yet</p>
            <p className="text-xs mt-1" style={{ color: "#64748B" }}>
              Open a job and click <strong>Geocode address</strong> to place it on the map.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
